import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { getOutletTransactionMandatesAsync } from './getOutletTransactionMandates';

interface ICreateOutletTransactionMandateRequest {
  name: string;
  categoryId: string;
  minimumAmount: number;
  pin: string;
  outletId: string;
  byPassAdminTransactions: boolean;
  approvalFlow: 'sequential' | 'flat'; //flat or sequential
  isAllTransactions: boolean;
  amounts: {
    approvers: {
      teamMemberIds: string[];
    }[];
    maximumAmount?: number;
  }[];
}

export interface IOutletTransactionMandateResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    businessId?: string;
    categoryId?: string;
    name?: string;
    minimumAmount?: string;
    description?: string;
    isActive?: boolean;
    amounts?: {
      id?: string;
      outletApprovers?: {
        transactionAmountId?: string;
        teamMemberId?: string;
        approverLevel?: number;
        createdAt?: string;
        updatedAt?: string;
      }[];
      isAllTransactions: boolean;
      maximumAmount?: string;
      mandateId?: string;
      createdAt: string;
      updatedAt: string;
    }[];
    outletId: string;
    createdAt: string;
    updatedAt: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IOutletTransactionMandateResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createOutletTransactionMandateAsync = createAsyncThunk(
  'outletTransactionMandate/create',
  async (
    values: ICreateOutletTransactionMandateRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.post(
        '/transaction/outlet-mandates',
        values,
        config,
      );

      dispatch(getOutletTransactionMandatesAsync());

      return data;
    } catch (err) {
      const error = err as AxiosError<IOutletTransactionMandateResponse>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const outletTransactionMandateCreationSlice = createSlice({
  name: 'outletTransactionMandate/create',
  initialState,
  reducers: {
    resetOutletTransactionMandateCreation(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOutletTransactionMandateAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(
        createOutletTransactionMandateAsync.fulfilled,
        (state, action) => {
          state.status = 'success';
          state.value = action.payload as IOutletTransactionMandateResponse;
        },
      )
      .addCase(
        createOutletTransactionMandateAsync.rejected,
        (state, action) => {
          state.status = 'failed';
          state.value = action.payload as IOutletTransactionMandateResponse;
          state.error = action.error.message;
        },
      );
  },
});

export const { resetOutletTransactionMandateCreation } =
  outletTransactionMandateCreationSlice.actions;
export default outletTransactionMandateCreationSlice.reducer;
